import React, { useState, useEffect } from 'react';
import {
  Info as InfoIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { CircularProgress } from '@mui/material';

import WebsitePageWrapper from './WebsitePageWrapper';
import { useStoreWebsite } from '../hooks/useStoreWebsite';

const WebsiteDomainPage = ({ userOrganizationId }) => {
  return (
    <WebsitePageWrapper
      title="Domain Settings"
      subtitle="Configure your website domain and subdomain"
      userOrganizationId={userOrganizationId}
    >
      {DomainContent}
    </WebsitePageWrapper>
  );
};

const DomainContent = ({ website, updateWebsite, isSaving, platformDomain, locationId }) => {
  const { verifyDomain } = useStoreWebsite(locationId);

  const [subdomainSlug, setSubdomainSlug] = useState(website?.subdomain_slug || '');
  const [orgSlug, setOrgSlug] = useState(website?.org_slug || '');
  const [customDomain, setCustomDomain] = useState(website?.custom_domain || '');
  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    setSubdomainSlug(website?.subdomain_slug || '');
    setOrgSlug(website?.org_slug || '');
    setCustomDomain(website?.custom_domain || '');
  }, [website]);

  const handleSave = async () => {
    await updateWebsite({
      subdomain_slug: subdomainSlug,
      org_slug: orgSlug,
      custom_domain: customDomain,
    });
  };

  const handleVerifyDomain = async () => {
    setIsVerifying(true);
    try {
      await verifyDomain();
    } finally {
      setIsVerifying(false);
    }
  };

  const cnameTarget = `sites.${platformDomain}`;
  const constructedSubdomain = `https://${subdomainSlug}-${orgSlug}.${platformDomain}`;

  return (
    <>
      <div className="website-page-content">
        {/* Platform Subdomain */}
        <div className="website-card">
          <div className="website-card-header">
            <h2 className="website-card-title">Platform Subdomain</h2>
            <p className="website-card-subtitle">
              Your website can be accessible at this URL. You can customize the subdomain and organization parts.
            </p>
          </div>

          <div className="website-card-body">
            <div className="website-form-group">
              <label className="website-form-label">Subdomain Components</label>
              <div className="website-domain-builder">
                <span className="website-domain-static">https://</span>
                <input
                  type="text"
                  value={subdomainSlug}
                  onChange={(e) => setSubdomainSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  placeholder="location"
                  className="website-domain-input"
                />
                <span className="website-domain-static">-</span>
                <input
                  type="text"
                  value={orgSlug}
                  onChange={(e) => setOrgSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  placeholder="store-name"
                  className="website-domain-input wide"
                />
                <span className="website-domain-static">.{platformDomain}</span>
              </div>

              <div className="website-domain-preview">
                <span className="website-domain-preview-label">Preview:</span>
                <span className="website-domain-preview-url">{constructedSubdomain}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Custom Domain */}
        <div className="website-card">
          <div className="website-card-header">
            <h2 className="website-card-title">Custom Domain (Optional)</h2>
            <p className="website-card-subtitle">
              Connect your own domain to your store website. For example: www.yourstore.com
            </p>
          </div>

          <div className="website-card-body">
            <div className="website-form-group">
              <label className="website-form-label">Custom Domain URL</label>
              <input
                type="text"
                value={customDomain}
                onChange={(e) => setCustomDomain(e.target.value)}
                placeholder="www.yourdomain.com"
                className="website-form-input"
              />
              <p className="website-form-hint">
                Enter the full domain name you want to use (including www if applicable)
              </p>
            </div>

            {/* Verification Status */}
            {website?.custom_domain && (
              <div className={`website-verification-status ${website.custom_domain_verified ? 'verified' : 'pending'}`}>
                {website.custom_domain_verified ? (
                  <>
                    <CheckIcon />
                    <span>Domain verified and active</span>
                  </>
                ) : (
                  <>
                    <WarningIcon />
                    <span>Domain pending verification</span>
                    <button
                      onClick={handleVerifyDomain}
                      disabled={isVerifying}
                      className="website-verify-btn"
                    >
                      {isVerifying ? <CircularProgress size={14} /> : 'Verify Now'}
                    </button>
                  </>
                )}
              </div>
            )}

            {/* DNS Instructions */}
            {customDomain && (
              <div className="website-dns-instructions">
                <div className="website-dns-header">
                  <InfoIcon />
                  <h4>DNS Configuration Required</h4>
                </div>
                <p>
                  To connect your custom domain, you'll need to add the following DNS records at your domain registrar:
                </p>
                <div className="website-dns-record">
                  <div className="website-dns-row">
                    <span className="website-dns-label">Type:</span>
                    <span className="website-dns-value">CNAME</span>
                  </div>
                  <div className="website-dns-row">
                    <span className="website-dns-label">Name:</span>
                    <span className="website-dns-value">{customDomain.replace('www.', '')}</span>
                  </div>
                  <div className="website-dns-row">
                    <span className="website-dns-label">Value:</span>
                    <span className="website-dns-value">{cnameTarget}</span>
                  </div>
                </div>
                <p className="website-dns-note">
                  DNS changes can take up to 48 hours to propagate globally.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Help Section */}
        <div className="website-card info-card">
          <div className="website-card-header">
            <div className="website-card-title-with-icon">
              <InfoIcon className="website-card-title-icon" />
              <h2 className="website-card-title">About Domains</h2>
            </div>
          </div>
          <div className="website-card-body">
            <ul className="website-info-list-card">
              <li>Your platform subdomain is free and works immediately</li>
              <li>Custom domains require you to own and configure the domain at your registrar</li>
              <li>SSL certificates are automatically provisioned for both subdomains and custom domains</li>
              <li>Need help? Contact support for assistance with domain configuration</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Fixed Action Bar */}
      <div className="website-action-bar">
        <button className="action-btn primary" onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Domain Settings'}
        </button>
      </div>
    </>
  );
};

export default WebsiteDomainPage;
